import type { TicketOptionWithQuantity, Translations } from '@repo/types';
import type { SummaryPriceLineData } from './summary-mapping';

/**
 * Build the "1 voksen + 1 barn" guest-breakdown label used when collapsing
 * adult (id '0') and child (id '1') Voksne/Barn picks into a single
 * accommodation summary line.
 *
 * Falls back to "1 voksen" if both counts are 0 — shouldn't reach the booking
 * flow but keeps the UI from rendering an empty label if it ever does.
 */
export function getAccommodationGuestLabel(
    adults: number,
    children: number,
    t: Translations,
): string {
    const parts: string[] = [];
    if (adults > 0) parts.push(`${adults} ${t.adult.num(adults)}`);
    if (children > 0) parts.push(`${children} ${t.children.num(children)}`);
    return parts.join(' + ') || `1 ${t.adult.num(1)}`;
}

/**
 * Read adult/child quantities from a flat ticketOptions array (cart item or
 * BookingContext quantities). Adults are id '0', children are id '1' — set in
 * `productFromBilberryAccommodation`.
 */
export function getAccommodationGuestCounts(quantities: TicketOptionWithQuantity[]): {
    adults: number;
    children: number;
} {
    return {
        adults: quantities.find((q) => q.id === '0')?.quantity ?? 0,
        children: quantities.find((q) => q.id === '1')?.quantity ?? 0,
    };
}

/**
 * Collapse a `useSummaryPriceLines` result for accommodation into a single
 * summary line whose label shows the Voksne/Barn breakdown. Visbook returns
 * the booking total on each ticketType, so each ticket option with quantity>0
 * would otherwise render its own line at the full total — duplicating the
 * displayed price.
 *
 * Returns the input unchanged when there are no lines or it isn't an
 * accommodation context.
 */
export function mergeAccommodationSummaryLines(
    rawLines: SummaryPriceLineData[],
    quantities: TicketOptionWithQuantity[],
    t: Translations,
): SummaryPriceLineData[] {
    if (rawLines.length === 0) return rawLines;
    const { adults, children } = getAccommodationGuestCounts(quantities);
    return [{ ...rawLines[0], ticketName: getAccommodationGuestLabel(adults, children, t) }];
}
